import React, { useEffect } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Chip, Tooltip, Typography } from '@mui/material';
import { ColumnHeader, SimpleTable } from './components/SimpleTable';
import {
  FiltersBar,
  SelectOTPs,
  SelectSubscriptionStatus,
} from './CrmFiltersBar';
import TOOLTIPS from './tooltips';
import { SubscriptionStatus } from './components/SubscriptionStatus';
import { trackEvent } from '@services/tracking';

export type OtpDto = {
  id: number;
  name: string;
  email: string;
  isEmailVerified: boolean;
  marketingConsent: boolean;
  status: string;
  otpOffers: Array<{ id: number; name: string }>;
};

const columns = [
  {
    title: 'Name',
    field: 'name',
    width: '15%',
    cellStyle: { width: '15%' },
  },
  {
    title: <ColumnHeader label="Email" title={TOOLTIPS.email} />,
    width: '25%',
    cellStyle: { width: '25%' },
    render: (row: OtpDto) => (
      <div className="flex items-center">
        {row.isEmailVerified && row.marketingConsent ? (
          <Icon icon={faBadgeCheck} className="text-primary mr-2" />
        ) : null}
        <span className="select-all">{row.email}</span>
      </div>
    ),
  },
  {
    title: <ColumnHeader label="Marketing" title={TOOLTIPS.marketing} />,
    width: '8%',
    cellStyle: { width: '8%' },
    render: (row: OtpDto) => <div>{row.marketingConsent ? 'Yes' : 'No'}</div>,
  },
  {
    title: <ColumnHeader label="Subscription" title={TOOLTIPS.statusOtp} />,
    width: '12%',
    cellStyle: { width: '12%' },
    render: (row: OtpDto) => (
      <div>
        <SubscriptionStatus subscriptionStatus={row.status} />
      </div>
    ),
  },
  {
    title: <ColumnHeader label="One-Time Purchases" title={TOOLTIPS.otp} />,
    render: (row: OtpDto) => (
      <div className="flex flex-wrap gap-2">
        {row.otpOffers.map((otp) => (
          <Tooltip key={otp.id} title={otp.name}>
            <Chip
              label={otp.name}
              className="border border-solid border-[#ECEDF2] bg-white text-[#151E34] h-[26px] text-sm max-w-[200px]"
            />
          </Tooltip>
        ))}
      </div>
    ),
  },
].map((c) => ({ ...c, sorting: false }));

export type OtpTableProps = {
  data: OtpDto[];
  totalResults: number;
};
export function OtpTable({ data, totalResults, ...props }: OtpTableProps) {
  useEffect(() => {
    trackEvent({
      type: 'Viewed OTP Table',
      payload: {},
    });
  }, []);

  return (
    <div className="max-w-full">
      <FiltersBar totalResults={totalResults}>
        <SelectOTPs />
        <SelectSubscriptionStatus />
      </FiltersBar>
      <div className="my-3 text-sm text-[#8A8E99]">
        <Typography>
          Contact information is shown for all users. The Marketing column
          indicates whether you have permission to email a user for promotional
          or non-support purposes.
        </Typography>
      </div>
      <SimpleTable {...props} columns={columns} data={data} />
    </div>
  );
}
